/**
 * WorkItemActionModal - Log actions taken on work items
 *
 * Provides explicit action tracking with outcomes.
 * No magic - user always knows what action is being logged.
 *
 * Action Types:
 * - email_sent: Email was sent
 * - phone_call: Phone call was made
 * - sms_sent: SMS was sent
 * - in_person: Handled in person
 * - scheduled: Scheduled for later (RDV)
 * - escalated: Escalated to supervisor
 * - deferred: Intentionally postponed
 * - documented: Added documentation
 * - resolved: Issue resolved
 * - other: Other action
 *
 * Outcomes:
 * - resolved: Fully resolved (marks work item DONE)
 * - pending_response: Waiting for response
 * - needs_followup: Requires follow-up
 * - blocked: Cannot proceed (marks work item BLOCKED)
 * - in_progress: Work started (marks work item IN_PROGRESS)
 */
import React, { useState } from 'react';
import {
  X,
  Mail,
  Phone,
  MessageSquare,
  Users,
  Calendar,
  ArrowUp,
  Clock,
  FileText,
  CheckCircle,
  HelpCircle,
  Loader2,
  Send,
} from 'lucide-react';
import { pipelineAPI } from '../../api/index.js';

const ACTION_TYPES = [
  { value: 'email_sent', label: 'Email envoyé', icon: Mail, color: 'blue' },
  { value: 'phone_call', label: 'Appel téléphonique', icon: Phone, color: 'green' },
  { value: 'sms_sent', label: 'SMS envoyé', icon: MessageSquare, color: 'purple' },
  { value: 'in_person', label: 'En personne', icon: Users, color: 'amber' },
  { value: 'scheduled', label: 'RDV planifié', icon: Calendar, color: 'indigo' },
  { value: 'escalated', label: 'Escaladé', icon: ArrowUp, color: 'red' },
  { value: 'deferred', label: 'Reporté', icon: Clock, color: 'gray' },
  { value: 'documented', label: 'Documenté', icon: FileText, color: 'teal' },
  { value: 'resolved', label: 'Résolu', icon: CheckCircle, color: 'emerald' },
  { value: 'other', label: 'Autre', icon: HelpCircle, color: 'slate' },
];

const OUTCOMES = [
  {
    value: 'resolved',
    label: 'Résolu',
    description: 'Le problème est complètement résolu',
    color: 'emerald',
    effect: '→ Tâche marquée TERMINÉE',
  },
  {
    value: 'in_progress',
    label: 'En cours',
    description: 'Travail commencé, pas encore terminé',
    color: 'blue',
    effect: '→ Tâche marquée EN COURS',
  },
  {
    value: 'pending_response',
    label: 'Attente réponse',
    description: "En attente d'une réponse externe",
    color: 'amber',
    effect: null,
  },
  {
    value: 'needs_followup',
    label: 'Suivi requis',
    description: 'Nécessite un suivi ultérieur',
    color: 'orange',
    effect: null,
  },
  {
    value: 'blocked',
    label: 'Bloqué',
    description: 'Impossible de continuer',
    color: 'red',
    effect: '→ Tâche marquée BLOQUÉE',
  },
];

function WorkItemActionModal({ isOpen, onClose, workItem, onSuccess, onMessage }) {
  const [actionType, setActionType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [followupDate, setFollowupDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !workItem) return null;

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!actionType) {
      setError('Veuillez sélectionner un type d\'action');
      return;
    }

    if (!outcome) {
      setError('Veuillez sélectionner un résultat');
      return;
    }

    setSaving(true);

    try {
      await pipelineAPI.logWorkItemAction(workItem.id, {
        action_type: actionType,
        outcome,
        action_note: actionNote || null,
        contact_name: contactName || null,
        contact_method: contactMethod || null,
        followup_date: followupDate || null,
      });

      const outcomeLabel = OUTCOMES.find(o => o.value === outcome)?.label;
      const actionLabel = ACTION_TYPES.find(a => a.value === actionType)?.label;

      onMessage?.({
        title: 'Action enregistrée',
        message: `${actionLabel} → ${outcomeLabel}`,
        type: 'success',
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to log action:', err);
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const selectedAction = ACTION_TYPES.find(a => a.value === actionType);
  const selectedOutcome = OUTCOMES.find(o => o.value === outcome);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Enregistrer une action</h2>
            <p className="text-sm text-gray-600 mt-1 line-clamp-1">{workItem.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Action Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Quelle action avez-vous effectuée ?
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {ACTION_TYPES.map(action => {
                const Icon = action.icon;
                const isSelected = actionType === action.value;
                return (
                  <button
                    key={action.value}
                    type="button"
                    onClick={() => setActionType(action.value)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      isSelected
                        ? `border-${action.color}-500 bg-${action.color}-50 text-${action.color}-700`
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 mx-auto mb-1 ${
                        isSelected ? `text-${action.color}-600` : 'text-gray-400'
                      }`}
                    />
                    <span className="text-xs font-medium">{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Outcome Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Quel est le résultat ?
            </label>
            <div className="space-y-2">
              {OUTCOMES.map(o => {
                const isSelected = outcome === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setOutcome(o.value)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? `border-${o.color}-500 bg-${o.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span
                          className={`font-medium ${
                            isSelected ? `text-${o.color}-700` : 'text-gray-700'
                          }`}
                        >
                          {o.label}
                        </span>
                        <p className="text-xs text-gray-500">{o.description}</p>
                      </div>
                      {o.effect && (
                        <span className="text-xs text-gray-400 italic">{o.effect}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contact Info (optional, for communication actions) */}
          {['email_sent', 'phone_call', 'sms_sent', 'in_person'].includes(actionType) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personne contactée (optionnel)
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}
                  placeholder="Nom"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coordonnées (optionnel)
                </label>
                <input
                  type="text"
                  value={contactMethod}
                  onChange={e => setContactMethod(e.target.value)}
                  placeholder="Email ou téléphone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Followup Date (for pending/needs_followup outcomes) */}
          {['pending_response', 'needs_followup', 'scheduled'].includes(outcome) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de suivi (optionnel)
              </label>
              <input
                type="date"
                value={followupDate}
                onChange={e => setFollowupDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (optionnel)
            </label>
            <textarea
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              rows={3}
              placeholder="Détails de l'action, résumé de la conversation..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Summary */}
          {selectedAction && selectedOutcome && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Résumé :</strong> {selectedAction.label} → {selectedOutcome.label}
                {selectedOutcome.effect && (
                  <span className="text-blue-600"> {selectedOutcome.effect}</span>
                )}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !actionType || !outcome}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Enregistrer l'action
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WorkItemActionModal;
